import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { fetchBirimler, createBirim, updateBirim, deleteBirim, reorderBirimler } from '../services/birimService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import './BirimYonetimiModal.css';

const emptyForm = () => ({
  kod: '',
  ad: '',
  sira: 0,
  aktif: true,
  ondalikKullan: true,
});

const birimRowId = (b) => String(b.id || b._id);

function SortableBirimRow({ birim, onEdit, onDelete }) {
  const id = birimRowId(birim);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className={isDragging ? 'birim-row--dragging' : ''}>
      <td className="birim-drag-cell">
        <button
          type="button"
          className="birim-drag-handle"
          {...attributes}
          {...listeners}
          aria-label="Sırayı değiştirmek için sürükleyin"
          title="Sırayı değiştirmek için sürükleyin"
        >
          <i className="fas fa-grip-vertical" aria-hidden="true" />
        </button>
      </td>
      <td>{birim.kod}</td>
      <td>{birim.ad}</td>
      <td>{birim.sira}</td>
      <td>{birim.aktif !== false ? 'Evet' : 'Hayır'}</td>
      <td>{birim.ondalikKullan !== false ? 'Evet' : 'Hayır'}</td>
      <td className="table-actions islemler-sutunu">
        <button
          type="button"
          className="category-action-btn edit-btn"
          onClick={() => onEdit(birim)}
          title="Düzenle"
        >
          <i className="fas fa-edit" aria-hidden="true" />
        </button>
        {birim.kod !== 'ADET' && (
          <button
            type="button"
            className="category-action-btn delete-btn"
            onClick={() => onDelete(birim)}
            title="Sil"
          >
            <i className="fas fa-trash-alt" aria-hidden="true" />
          </button>
        )}
      </td>
    </tr>
  );
}

export default function BirimYonetimiModal({ open, onClose, onSaved, variant = 'modal' }) {
  const { confirm } = useConfirmDialog();
  const embedded = variant === 'embedded';
  const [liste, setListe] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const sortableIds = useMemo(() => liste.map((b) => birimRowId(b)), [liste]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetchBirimler();
      const arr = r?.veri?.birimler || [];
      setListe(Array.isArray(arr) ? arr : []);
    } catch (e) {
      setError(e?.message || 'Birimler yüklenemedi');
      setListe([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    setEditingId(null);
    setForm(emptyForm());
  }, [open, load]);

  const startEdit = (b) => {
    setEditingId(b.id || b._id);
    setForm({
      kod: b.kod || '',
      ad: b.ad || '',
      sira: typeof b.sira === 'number' ? b.sira : 0,
      aktif: b.aktif !== false,
      ondalikKullan: b.ondalikKullan !== false,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        kod: form.kod.trim().toUpperCase(),
        ad: form.ad.trim(),
        sira: Number(form.sira) || 0,
        aktif: !!form.aktif,
        ondalikKullan: !!form.ondalikKullan,
      };
      if (!payload.kod || !payload.ad) {
        setError('Kod ve ad zorunludur.');
        setSaving(false);
        return;
      }
      if (editingId) {
        const r = await updateBirim(editingId, payload);
        if (!r?.basarili) throw new Error(r?.mesaj || 'Güncellenemedi');
      } else {
        const r = await createBirim(payload);
        if (!r?.basarili) throw new Error(r?.mesaj || 'Oluşturulamadı');
      }
      if (window.showNotification) {
        window.showNotification('success', editingId ? 'Birim güncellendi' : 'Birim eklendi');
      }
      cancelEdit();
      await load();
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (b) => {
    const id = b.id || b._id;
    if (!id) return;
    if (b.kod === 'ADET') {
      if (window.showNotification) window.showNotification('error', 'Adet birimi silinemez');
      return;
    }
    const ok = await confirm({
      title: 'Birimi sil',
      message: `"${b.ad}" birimini silmek istediğinize emin misiniz?`,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) return;
    setError('');
    try {
      const r = await deleteBirim(id);
      if (!r?.basarili) throw new Error(r?.mesaj || 'Silinemedi');
      if (window.showNotification) window.showNotification('success', 'Birim silindi');
      if (editingId === id) cancelEdit();
      await load();
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.message || 'Silinemedi');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || reordering) return;

    const oldIndex = liste.findIndex((b) => birimRowId(b) === String(active.id));
    const newIndex = liste.findIndex((b) => birimRowId(b) === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(liste, oldIndex, newIndex);
    const prevListe = liste;
    setListe(reordered);
    setReordering(true);
    setError('');

    try {
      const r = await reorderBirimler(reordered.map((b) => birimRowId(b)));
      if (!r?.basarili) throw new Error(r?.mesaj || 'Sıra güncellenemedi');
      const arr = r?.veri?.birimler;
      if (Array.isArray(arr)) setListe(arr);
      if (window.showNotification) window.showNotification('success', 'Birim sırası güncellendi');
      if (onSaved) onSaved();
    } catch (err) {
      setListe(prevListe);
      setError(err?.message || 'Sıra güncellenemedi');
      if (window.showNotification) window.showNotification('error', err?.message || 'Sıra güncellenemedi');
    } finally {
      setReordering(false);
    }
  };

  if (!open) return null;

  const formBlock = (
    <>
      {embedded ? (
        <div style={{ marginBottom: 16 }}>
          <h2 id="birim-yonetimi-embedded-title" style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600, color: 'var(--text-dark)' }}>
            Birim yönetimi
          </h2>
          <p style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.5, color: 'var(--text-gray)' }}>
            Parça içerikleri bu birimlerden birine bağlanır. <strong>Adet</strong> tam sayı; kg, m vb. için
            &quot;Ondalık kullan&quot;ı işaretleyin. Sıralamak için satırdaki tutamacı sürükleyin.
          </p>
        </div>
      ) : (
        <div className="modal-header">
          <h2 id="birim-modal-title">Birim yönetimi</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
      )}

      <form
        className={embedded ? 'birim-yonetimi-embedded__form' : 'content-item-form-modal__form'}
        onSubmit={handleSubmit}
      >
        <div className={embedded ? 'birim-yonetimi-embedded__scroll' : 'content-item-form-modal__scroll'}>
          {!embedded ? (
            <p style={{ margin: '0 0 16px', fontSize: 12, lineHeight: 1.5, color: 'var(--text-gray)' }}>
              Parça içerikleri bu birimlerden birine bağlanır. <strong>Adet</strong> tam sayı; kg, m vb. için
              &quot;Ondalık kullan&quot;ı işaretleyin. Sıralamak için satırdaki tutamacı sürükleyin.
            </p>
          ) : null}

            <div className="inline-select-row">
              <label className="form-field">
                <span>Kod *</span>
                <input
                  type="text"
                  value={form.kod}
                  onChange={(e) => setForm((p) => ({ ...p, kod: e.target.value }))}
                  maxLength={24}
                  placeholder="Örn. KG"
                  disabled={!!editingId && form.kod === 'ADET'}
                />
              </label>
              <label className="form-field">
                <span>Ad *</span>
                <input
                  type="text"
                  value={form.ad}
                  onChange={(e) => setForm((p) => ({ ...p, ad: e.target.value }))}
                  maxLength={80}
                  placeholder="Görünen ad"
                />
              </label>
              <label className="form-field">
                <span>Sıra</span>
                <input
                  type="number"
                  value={form.sira}
                  onChange={(e) => setForm((p) => ({ ...p, sira: e.target.value }))}
                />
              </label>
            </div>

            <label className="form-field checkbox-field" style={{ marginTop: 12 }}>
              <input
                type="checkbox"
                checked={form.aktif}
                onChange={(e) => setForm((p) => ({ ...p, aktif: e.target.checked }))}
              />
              <span>Aktif</span>
            </label>
            <label className="form-field checkbox-field">
              <input
                type="checkbox"
                checked={form.ondalikKullan}
                onChange={(e) => setForm((p) => ({ ...p, ondalikKullan: e.target.checked }))}
              />
              <span>Ondalık kullan (kapalıysa miktar tam sayı)</span>
            </label>

            {error && (
              <div className="form-error" style={{ marginTop: 12 }}>
                <i className="fas fa-exclamation-circle" aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            <div className="form-group" style={{ marginTop: 20 }}>
              <label>Birim listesi</label>
              {reordering ? (
                <p className="birim-reorder-hint" role="status">
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Sıra kaydediliyor…
                </p>
              ) : null}
              {loading ? (
                <div className="loading-state" style={{ padding: '24px 0' }}>
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                  <span>Yükleniyor…</span>
                </div>
              ) : (
                <div className="table-container">
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <table className="admins-table birim-sortable-table">
                      <thead>
                        <tr>
                          <th className="birim-drag-cell" aria-label="Sıra" />
                          <th>Kod</th>
                          <th>Ad</th>
                          <th>Sıra</th>
                          <th>Aktif</th>
                          <th>Ondalık</th>
                          <th className="islemler-sutunu">İşlem</th>
                        </tr>
                      </thead>
                      <tbody>
                        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                          {liste.map((b) => (
                            <SortableBirimRow
                              key={birimRowId(b)}
                              birim={b}
                              onEdit={startEdit}
                              onDelete={handleDelete}
                            />
                          ))}
                        </SortableContext>
                      </tbody>
                    </table>
                  </DndContext>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            {!embedded && (
              <button type="button" className="secondary-btn" onClick={onClose}>
                İptal
              </button>
            )}
            {editingId && (
              <button type="button" className="secondary-btn" onClick={cancelEdit}>
                Yeni birim
              </button>
            )}
            <button type="submit" className="primary-btn" disabled={saving || reordering}>
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                  <span>Kaydediliyor…</span>
                </>
              ) : (
                <>
                  <i className="fas fa-save" aria-hidden="true" />
                  <span>{editingId ? 'Güncelle' : 'Ekle'}</span>
                </>
              )}
            </button>
          </div>
        </form>
    </>
  );

  if (embedded) {
    return (
      <div className="card birim-yonetimi-embedded">
        {formBlock}
      </div>
    );
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="modal-content content-item-form-modal birim-yonetimi-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {formBlock}
      </div>
    </div>
  );
}
